import { badgeApolloClient } from "@/utils/graphql-client-badges";
import {
  Get_GenerateBadgePdfByUuidQuery,
  Get_GenerateBadgePdfByIdQuery,
  Get_GenerateBadgePdfByUuidQueryVariables,
  Get_GenerateBadgePdfByIdQueryVariables,
  SignFiskalyReceiptMutation,
  SignFiskalyReceiptMutationVariables,
  Get_GenerateGiftcardPdfByIdQuery,
  Get_GenerateGiftcardPdfByIdQueryVariables,
  Get_GenerateGiftcardPdfByUuidQuery,
  Get_GenerateGiftcardPdfByUuidQueryVariables,
  GetOrderSessionsQuery,
  GetOrderSessionsQueryVariables,
} from "@/types/gql/generated/graphql-badges";
import {
  GET_GenerateBadgePdfById,
  GET_GenerateBadgePdfByUuid,
  GET_GenerateGiftcardPdfById,
  GET_GenerateGiftcardPdfByUuid,
  GET_OrderSessionById,
  Mutation_CompleteOrder,
  Mutation_SignAustriaReceipt,
  Mutation_SignReceipt,
  Mutation_StartOrder,
} from "./queries-badges/generate.graphql";
import { LogLevel, LogService } from "@/services/logging";
import { BaseOrderSession } from "@/types/db/product-slot-booking";
import { OrderType, OrderSession } from "@smeetz/order-common";
import { FetchResult } from "apollo-link";

/**
 * generates and returns a base64 pdf for the membership badge
 *
 * @param pssUuid ticket uuid
 */
export async function getBadgePdf(data: {
  pssUuid?: string | null;
  pssId?: number | null;
  isLocarno?: boolean;
}) {
  const { pssUuid, pssId, isLocarno } = data;
  if (!pssUuid && !pssId) {
    const error = new Error();
    error.name = "BadgePdfError";
    error.message = "No id or uuid provided";
    throw error;
  }
  let res;
  if (pssId) {
    res = await badgeApolloClient.query<
      Get_GenerateBadgePdfByIdQuery,
      Get_GenerateBadgePdfByIdQueryVariables
    >({
      query: GET_GenerateBadgePdfById,
      variables: { ticketId: String(pssId), locarno: Boolean(isLocarno) },
    });
  } else if (pssUuid) {
    res = await badgeApolloClient.query<
      Get_GenerateBadgePdfByUuidQuery,
      Get_GenerateBadgePdfByUuidQueryVariables
    >({
      query: GET_GenerateBadgePdfByUuid,
      variables: { ticketUuid: pssUuid, locarno: Boolean(isLocarno) },
    });
  }
  const base64pdf = res?.data
    ? res.data.generateMembershipBadgePdf?.pdfBase64
    : null;
  if (!base64pdf) {
    const error = new Error();
    error.name = "BadgePdfError";
    error.message = res?.errors ? res.errors[0].message : "";
    LogService.info(
      `Error when generating badge pdf for pss: ${pssUuid}`,
      error,
      LogLevel.Error
    );
    throw error;
  }
  return base64pdf;
}

export async function signReceipt(schema: any) {
  // const resp = await badgeApolloClient.query<
  //     Mutation_SignReceiptQuery,
  //     Mutation_SignReceiptQueryVariables
  //   >({
  //     query: Mutation_SignReceipt,
  //     // variables: { ticketUuid: pssUuid },
  //   });
  const resp = await badgeApolloClient.mutate<
    SignFiskalyReceiptMutation,
    SignFiskalyReceiptMutationVariables
  >({
    mutation: Mutation_SignReceipt,
    variables: {
      groupId: schema.groupId,
      deviceUid: schema.deviceUid,
      receiptUuid: schema.receiptUuid,
      receiptDate: schema.date,
      currency: schema.currency,
      fullAmountInclVat: schema.full_amount_incl_vat,
      paymentTypes: JSON.stringify(
        schema.payment_types.map((p: any) => {
          return { name: p.name, amount: p.amount };
        })
      ) /*[{name:"CASH",amount:"10.00"}])*/,
      vatAmounts: JSON.stringify(
        schema.vat_amounts.map((v: any) => {
          return {
            vat_rate: v.vat_rate,
            percentage: v.percentage,
            incl_vat: v.incl_vat,
            excl_vat: v.excl_vat,
            vat: v.vat,
          };
        })
      ) /*[{vat_rate:"STANDARD",percentage:100,incl_vat:"-123.45",excl_vat:"-123.45",vat:"-123.45"}]),*/,
      lines: JSON.stringify(schema.lines) /*[{text:"ticket"}])*/,
    },
  });
  return resp;
}

/**
 * generates and returns a base64 pdf for the giftcard
 *
 * @param pssUuid ticket uuid
 */
export async function getGiftcardPdf(data: {
  pssUuid?: string | null;
  pssId?: number | null;
}) {
  const { pssUuid, pssId } = data;
  if (!pssUuid && !pssId) {
    const error = new Error();
    error.name = "GiftcardPdfError";
    error.message = "No id or uuid provided";
    throw error;
  }
  let res;
  if (pssId) {
    res = await badgeApolloClient.query<
      Get_GenerateGiftcardPdfByIdQuery,
      Get_GenerateGiftcardPdfByIdQueryVariables
    >({
      query: GET_GenerateGiftcardPdfById,
      variables: { ticketId: String(pssId) },
    });
  } else if (pssUuid) {
    res = await badgeApolloClient.query<
      Get_GenerateGiftcardPdfByUuidQuery,
      Get_GenerateGiftcardPdfByUuidQueryVariables
    >({
      query: GET_GenerateGiftcardPdfByUuid,
      variables: { ticketUuid: pssUuid },
    });
  }
  const base64pdf = res?.data ? res.data.generateGiftcardPdf?.pdfBase64 : null;
  if (!base64pdf) {
    const error = new Error();
    error.name = "GiftcardPdfError";
    error.message = res?.errors ? res.errors[0].message : "";
    LogService.info(
      `Error when generating giftcard pdf for pss: ${pssUuid}`,
      error,
      LogLevel.Error
    );
    throw error;
  }
  return base64pdf;
}

export async function startOrder(data: {
  deviceUid: string;
  orderSession: BaseOrderSession;
}) {
  const { deviceUid, orderSession } = data;
  const resp = await badgeApolloClient.mutate({
    mutation: Mutation_StartOrder,
    variables: {
      deviceUid,
      orderSession,
    },
  });
  return resp;
}

export async function completeOrder(
  data: {
    deviceUid: string;
    orderType: OrderType;
    orderSession: OrderSession;
  },
  timeoutMs = 5000
) {
  const { deviceUid, orderType, orderSession } = data;

  const mutationPromise = badgeApolloClient.mutate({
    mutation: Mutation_CompleteOrder,
    variables: {
      deviceUid,
      orderType,
      orderSession,
    },
  });

  const timeoutPromise = new Promise((_, reject) =>
    setTimeout(() => {
      reject(new Error("Mutation request timed out"));
    }, timeoutMs)
  );

  const resp = await Promise.race([mutationPromise, timeoutPromise]);
  return resp as Promise<
    FetchResult<any, Record<string, any>, Record<string, any>>
  >; // type of mutation response
}

export async function getOrderSessionById(orderSessionId: string) {
  const resp = await badgeApolloClient.query<
    GetOrderSessionsQuery,
    GetOrderSessionsQueryVariables
  >({
    query: GET_OrderSessionById,
    variables: { orderSessionId },
  });
  if (resp?.errors?.length) {
    const error = new Error();
    error.name = "GetOrderSessionByIdError";
    error.message = resp.errors[0].message;
    LogService.info(
      `Error when fetching order session by id ${orderSessionId}`,
      error,
      LogLevel.Error
    );
  }
  const orderSessions = resp?.data?.getOrderSessions;
  // Callers branch on total and items.length to decide whether a session was signed and where its
  // order lines are. Log what the certification DB actually answered, otherwise "absent from the
  // certification DB" and "query came back fine with nothing in it" are indistinguishable.
  LogService.info(
    `Certification DB answered for order session ${orderSessionId}`,
    {
      orderSessionId,
      total: orderSessions?.total,
      itemsCount: orderSessions?.items?.length,
      hasData: !!orderSessions,
      errors: resp?.errors?.map((e) => e.message),
    }
  );
  return orderSessions;
}

/**
 * Whether an order session was signed, which is the case when it exists in the certification DB.
 * Returns undefined when the lookup itself failed, so that fiscal decisions never read an
 * unreachable certification DB as "this session was never signed".
 * getOrderSessionById cannot express that difference: it returns undefined both for an absent
 * session and for a failed query, and rejects on network errors.
 */
export async function isOrderSessionSigned(
  orderSessionId: string
): Promise<boolean | undefined> {
  let resp;
  try {
    resp = await badgeApolloClient.query<
      GetOrderSessionsQuery,
      GetOrderSessionsQueryVariables
    >({
      query: GET_OrderSessionById,
      variables: { orderSessionId },
    });
  } catch (error) {
    LogService.info(
      `Signed state of order session ${orderSessionId} is undetermined: could not reach the certification DB`,
      { orderSessionId, error },
      LogLevel.Error
    );
    return;
  }
  const orderSessions = resp?.data?.getOrderSessions;
  if (!orderSessions) {
    LogService.info(
      `Signed state of order session ${orderSessionId} is undetermined: the certification DB returned no data`,
      { orderSessionId, errors: resp?.errors?.map((e) => e.message) },
      LogLevel.Error
    );
    return;
  }
  const signed = orderSessions.total > 0;
  // every fiscal decision downstream turns on this verdict, so record it explicitly
  LogService.info(
    `Order session ${orderSessionId} is ${signed ? "signed" : "not signed"}`,
    { orderSessionId, total: orderSessions.total }
  );
  return signed;
}

export async function signAustriaReceiptMutation(data: {
  deviceUid: string;
  orderSession: OrderSession;
}) {
  const { deviceUid, orderSession } = data;
  const resp = await badgeApolloClient.mutate({
    mutation: Mutation_SignAustriaReceipt,
    variables: {
      deviceUid,
      orderSession,
    },
  });
  return resp;
}
